import { ApiError } from '@daater/api';
import { normalizeCurrentAccountResponse } from './endpoints';
import type { ApiResponse, AccountResponse } from './types';

export type CurrentAccountAccessDecision = 'allow' | 'login' | 'payment' | 'error';

export type CurrentAccountAccessResult = {
  decision: CurrentAccountAccessDecision;
  account?: AccountResponse;
  amount?: number;
  userMessage?: string;
  technicalMessage?: string;
};

const toPositiveNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return undefined;
};

const normalizeAccount = (account?: AccountResponse | null) => {
  if (!account) return undefined;
  return normalizeCurrentAccountResponse(account);
};

/**
 * Un corte que se arregla solo: sin red, el límite de peticiones o el API
 * reiniciándose en un despliegue. Nada de eso dice que la sesión ya no sirva.
 */
export const isTransientApiError = (error: unknown): error is ApiError =>
  error instanceof ApiError && (error.status === 0 || error.status === 429 || error.status >= 500);

/**
 * La cuenta se reintenta ante el límite o un reinicio del API, que duran unos
 * segundos. Los fallos de red no: el cliente HTTP ya los repitió.
 */
export const shouldRetryAccountQuery = (failureCount: number, error: unknown): boolean =>
  isTransientApiError(error) && error.status !== 0 && failureCount < 3;

export const getCurrentAccountFromApiError = (error: unknown): AccountResponse | undefined => {
  if (!(error instanceof ApiError)) return undefined;

  const responseData = error.data as ApiResponse<AccountResponse> | undefined;
  return normalizeAccount(responseData?.data);
};

export const classifyCurrentAccountAccess = ({
  account,
  error,
}: {
  account?: AccountResponse | null;
  error?: unknown;
}): CurrentAccountAccessResult => {
  const normalizedAccount = normalizeAccount(account) ?? getCurrentAccountFromApiError(error);
  const amount = toPositiveNumber(normalizedAccount?.amount);
  const status = normalizedAccount?.status;
  const daysLeft = normalizedAccount?.daysLeft;
  const bodyStatusCode = normalizedAccount?.statusCode;

  if (error instanceof ApiError) {
    if (error.status === 401) {
      return { decision: 'login', account: normalizedAccount, amount };
    }

    if (error.status === 402) {
      return { decision: 'payment', account: normalizedAccount, amount };
    }

    if (bodyStatusCode === 402) {
      return { decision: 'payment', account: normalizedAccount, amount };
    }

    // Con la cuenta ya cargada, un corte pasajero al recargarla (por ejemplo al
    // volver a la pestaña) no tumba la app: se sigue con la cuenta que había.
    if (!(account && isTransientApiError(error))) {
      return {
        decision: 'error',
        account: normalizedAccount,
        amount,
        userMessage: error.userMessage ?? 'Ocurrió un error cargando la cuenta.',
        technicalMessage: error.technicalMessage ?? `Error ${error.status}: ${error.message}`,
      };
    }
  } else if (error) {
    const msg =
      error instanceof Error
        ? error.message
        : typeof error === 'string'
          ? error
          : 'Unexpected non-error value thrown.';
    return {
      decision: 'error',
      account: normalizedAccount,
      amount,
      userMessage: 'Ocurrió un error inesperado.',
      technicalMessage: msg,
    };
  }

  if (bodyStatusCode === 402) {
    return { decision: 'payment', account: normalizedAccount, amount };
  }

  if (status === 'suspended') {
    return { decision: 'payment', account: normalizedAccount, amount };
  }

  if (status === 'waiting') {
    return {
      decision: 'error',
      account: normalizedAccount,
      amount,
      userMessage: 'Tu cuenta está pendiente de aprobación.',
      technicalMessage: 'User status is waiting — account not yet approved.',
    };
  }

  if (status === 'paid' || status === 'open') {
    return { decision: 'allow', account: normalizedAccount, amount };
  }

  if (status === 'trial') {
    if (typeof daysLeft === 'number' && daysLeft < 0) {
      return { decision: 'payment', account: normalizedAccount, amount };
    }
    return { decision: 'allow', account: normalizedAccount, amount };
  }

  if (normalizedAccount) {
    return { decision: 'allow', account: normalizedAccount, amount };
  }

  return {
    decision: 'error',
    account: normalizedAccount,
    amount,
    userMessage: 'No se pudo cargar la información de la cuenta.',
    technicalMessage: 'No account data returned and no error was thrown.',
  };
};

export const buildPaymentRedirectSearch = (
  currentSearch: string,
  amount?: number
): Record<string, string> => {
  const params = new URLSearchParams(currentSearch);

  if (amount !== undefined) {
    params.set('amount', String(amount));
  }

  return Object.fromEntries(params.entries());
};

export const resolveCurrentAccountAccess = async (
  fetchAccount: () => Promise<AccountResponse>
): Promise<CurrentAccountAccessResult> => {
  try {
    const account = await fetchAccount();
    return classifyCurrentAccountAccess({ account });
  } catch (error) {
    return classifyCurrentAccountAccess({ error });
  }
};
